import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import {
	Alert,
	Box,
	Button,
	Chip,
	LinearProgress,
	Paper,
	Skeleton,
	Snackbar,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ScanEntryBundleModal } from "@/pages/sawlines/components/ScanEntryBundleModal";
import { useEmployeeNumber } from "../employee/EmployeeNumber";
import type { SawTabletTabProps } from "../sawTabletConfig";
import { TabletConfirm } from "../components/TabletConfirm";
import { LoadedThisShift } from "./components/LoadedThisShift";
import { MaterialQueueRow } from "./components/MaterialQueueRow";
import { WorkOrderStrip, WorkOrderSummary } from "./components/WorkOrderStrip";
import { sawEntryQueueKey, useSawEntryQueue } from "./hooks/useSawEntryQueue";
import { type LoadedPrompt, loadedPrompt } from "./loadedPrompt";
import {
	loadSawEntryScan,
	removeSawEntryScan,
	reorderSawEntryQueue,
	restoreSawEntryScan,
	type SawEntryLoaded,
	type SawEntryQueueEntry,
} from "./repositories/sawEntryQueueRepository";

const COLUMNS = ["#", "Tag", "Material"];

const unloadEffect = (row: SawEntryLoaded) =>
	[
		`Tag ${row.TagNo} goes back to the top of the queue.`,
		row.StartedWorkOrderNumber &&
			`Work order ${row.StartedWorkOrderNumber} goes back to Pending.`,
		row.CompletedWorkOrderNumber &&
			`Work order ${row.CompletedWorkOrderNumber} is reopened.`,
	]
		.filter(Boolean)
		.join(" ");

export const MaterialTab = ({ assetId }: SawTabletTabProps) => {
	const queue = useSawEntryQueue(assetId);
	const queryClient = useQueryClient();
	// one queue write at a time; restore only drives the Undo label
	const save = useMutation<
		unknown,
		Error,
		{ write: () => Promise<unknown>; restore?: boolean }
	>({
		mutationFn: ({ write }) => write(),
		onSettled: () =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: sawEntryQueueKey(assetId) }),
				queryClient.invalidateQueries({ queryKey: ["sawOverview"] }),
			]),
	});
	const { requireEmployee } = useEmployeeNumber();
	const [scanOpen, setScanOpen] = useState(false);
	const [asking, setAsking] = useState<{
		entry: SawEntryQueueEntry;
		employee: string;
		prompt: LoadedPrompt;
	} | null>(null);
	const [unloading, setUnloading] = useState<{
		row: SawEntryLoaded;
		employee: string;
	} | null>(null);
	const [snack, setSnack] = useState<{
		message: string;
		entryBundleId?: number;
	} | null>(null);
	const entries = queue.data?.entries ?? [];
	const busy = save.isLoading || queue.isError;
	const restoring = save.isLoading && save.variables?.restore === true;

	const run = (
		write: () => Promise<unknown>,
		options?: { onSuccess?: () => void; onError?: () => void },
		restore = false,
	) => {
		if (!busy) save.mutate({ write, restore }, options);
	};
	const remove = (
		entry: SawEntryQueueEntry,
		loaded: boolean,
		employee: string,
		workOrders?: LoadedPrompt,
	) => {
		setSnack(null);
		run(
			() =>
				loaded
					? loadSawEntryScan(assetId, entry.EntryBundleId, employee, workOrders)
					: removeSawEntryScan(assetId, entry.EntryBundleId, employee),
			{
				onSuccess: () =>
					setSnack({
						message: `Tag ${entry.TagNo} ${loaded ? "loaded" : "removed"}${workOrders?.done ?? ""}`,
						entryBundleId: entry.EntryBundleId,
					}),
			},
		);
	};

	return (
		<Stack gap={1.5} sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
			{queue.data && (
				<Paper sx={{ flexShrink: 0, overflow: "hidden" }}>
					<WorkOrderStrip running={queue.data.running} next={queue.data.next} />
				</Paper>
			)}
			<Stack
				direction={{ xs: "column", md: "row" }}
				gap={1.5}
				sx={{ flex: 1, minHeight: 0 }}
			>
				<Paper
					sx={{
						display: "flex",
						flexDirection: "column",
						flex: 3,
						minHeight: 0,
						minWidth: 0,
						overflow: "hidden",
					}}
				>
					<Stack
						direction="row"
						alignItems="center"
						gap={1.25}
						sx={{ p: 1.5, px: 2, borderBottom: 1, borderColor: "divider" }}
					>
						<Box>
							<Stack direction="row" alignItems="center" gap={1.25}>
								<Typography component="h2" variant="h6">
									Staged material
								</Typography>
								{queue.data && (
									<Chip
										label={`${entries.length} of ${queue.data.limit}`}
										color="success"
										variant="outlined"
										sx={{ fontSize: 16, fontWeight: 700, height: 34 }}
									/>
								)}
							</Stack>
							<Typography variant="caption" color="text.secondary">
								Top of the list goes into the saw first
							</Typography>
						</Box>
						<Button
							variant="contained"
							color="success"
							startIcon={<QrCodeScannerIcon />}
							onClick={() => setScanOpen(true)}
							disabled={save.isLoading || !queue.data || queue.isError}
							sx={{ ml: "auto", minHeight: 56, px: 2.5, fontSize: 16 }}
						>
							Scan tag
						</Button>
					</Stack>
					{save.isLoading && (
						<LinearProgress aria-label="Saving material queue" />
					)}
					{save.error && (
						<Alert severity="error" onClose={() => save.reset()}>
							{save.error.message}
						</Alert>
					)}
					{queue.error && (
						<Alert
							severity="error"
							action={
								<Button onClick={() => void queue.refetch()}>Retry</Button>
							}
						>
							{queue.error.message}
						</Alert>
					)}
					<Box
						sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
						aria-busy={queue.isLoading || save.isLoading}
					>
						{queue.isLoading ? (
							<Box
								role="status"
								aria-label="Loading material queue"
								sx={{ p: 2 }}
							>
								<Skeleton height={76} />
								<Skeleton height={76} />
								<Skeleton height={76} />
							</Box>
						) : (
							queue.data && (
								<Table
									sx={{
										"& th": {
											px: 1.5,
											py: 1.25,
											fontSize: 13,
											fontWeight: 700,
											letterSpacing: 0.5,
											textTransform: "uppercase",
											color: "text.secondary",
											bgcolor: "#fafbfc",
											borderBottomWidth: 2,
										},
										"& td": { px: 1.25, py: 0.75, height: 68, fontSize: 15 },
									}}
								>
									<TableHead>
										<TableRow>
											{COLUMNS.map((column) => (
												<TableCell key={column}>{column}</TableCell>
											))}
											<TableCell aria-label="Actions" />
										</TableRow>
									</TableHead>
									<TableBody>
										{entries.map((entry, index) => (
											<MaterialQueueRow
												key={entry.EntryBundleId}
												entry={entry}
												index={index}
												count={entries.length}
												busy={busy}
												onMove={(direction) =>
													requireEmployee((e) =>
														run(() =>
															reorderSawEntryQueue(
																assetId,
																entry.EntryBundleId,
																direction,
																e,
															),
														),
													)
												}
												onLoaded={() =>
													requireEmployee((employee) => {
														const prompt = loadedPrompt(
															entry,
															queue.data?.running,
															queue.data?.next,
														);
														if (prompt) setAsking({ entry, employee, prompt });
														else remove(entry, true, employee);
													})
												}
												onRemove={() =>
													requireEmployee((e) => remove(entry, false, e))
												}
											/>
										))}
										{entries.length === 0 && (
											<TableRow>
												<TableCell
													colSpan={COLUMNS.length + 1}
													sx={{ color: "text.secondary" }}
												>
													No material staged. Tap Scan tag.
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							)
						)}
					</Box>
				</Paper>
				<LoadedThisShift
					loaded={queue.data?.loaded}
					busy={busy}
					onUnload={(row) =>
						requireEmployee((employee) => setUnloading({ row, employee }))
					}
				/>
			</Stack>
			{asking && (
				<TabletConfirm
					title="WORK ORDER"
					subtitle={`Loading tag ${asking.entry.TagNo}`}
					message={asking.prompt.question}
					confirmLabel={asking.prompt.confirm}
					cancelLabel={asking.prompt.dismiss}
					onClose={(switchWorkOrders) => {
						setAsking(null);
						if (switchWorkOrders !== null)
							remove(
								asking.entry,
								true,
								asking.employee,
								switchWorkOrders ? asking.prompt : undefined,
							);
					}}
				>
					<Typography color="text.secondary" sx={{ fontSize: 16, mb: 2 }}>
						{asking.prompt.reason}
					</Typography>
					<Stack gap={1.5} sx={{ mb: 2.5 }}>
						{asking.prompt.complete && (
							<WorkOrderSummary
								action="Complete"
								workOrder={asking.prompt.complete}
							/>
						)}
						{asking.prompt.start && (
							<WorkOrderSummary
								action="Start"
								workOrder={asking.prompt.start}
							/>
						)}
					</Stack>
				</TabletConfirm>
			)}
			{unloading && (
				<TabletConfirm
					title="UNLOAD"
					subtitle={`Tag ${unloading.row.TagNo}`}
					message={`Unload tag ${unloading.row.TagNo}?`}
					confirmLabel="Unload"
					onClose={(confirmed) => {
						const { row, employee } = unloading;
						setUnloading(null);
						if (confirmed)
							run(
								() => restoreSawEntryScan(assetId, row.EntryBundleId, employee),
								{
									onSuccess: () =>
										setSnack({
											message: `Tag ${row.TagNo} unloaded, back in the queue`,
										}),
								},
							);
					}}
				>
					<Typography color="text.secondary" sx={{ fontSize: 16, mb: 2 }}>
						{unloadEffect(unloading.row)}
					</Typography>
				</TabletConfirm>
			)}
			<ScanEntryBundleModal
				open={scanOpen}
				assetId={assetId}
				onClose={() => setScanOpen(false)}
				requireEmployee={requireEmployee}
			/>
			<Snackbar
				open={!!snack}
				autoHideDuration={restoring ? null : 6000}
				onClose={(_, reason) => {
					if (reason !== "clickaway" && !restoring) setSnack(null);
				}}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				message={snack?.message}
				action={
					snack?.entryBundleId ? (
						<Button
							color="warning"
							disabled={busy}
							onClick={() =>
								requireEmployee((e) =>
									run(
										() => restoreSawEntryScan(assetId, snack.entryBundleId!, e),
										{
											onSuccess: () =>
												setSnack({ message: "Tag restored to the queue" }),
											onError: () => setSnack(null),
										},
										true,
									),
								)
							}
							sx={{ minHeight: 64, px: 2 }}
						>
							{restoring ? "Restoring…" : "Undo"}
						</Button>
					) : undefined
				}
			/>
		</Stack>
	);
};
